import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { experiences } from '../data';
import { experiencePriceLabel } from '../quote/experiencePriceLabel';
import CategoryTabs from './CategoryTabs';

const CATEGORY_ALIASES = {
  outings: ['outing', 'outings'],
  trainings: ['training', 'trainings'],
  speakers: ['speaker', 'speakers'],
  retreats: ['retreat', 'retreats'],
  volunteer: [
    'volunteer',
    'volunteer event',
    'volunteer events',
  ],
};

const normalizeValue = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const ExperienceBrowser = ({
  selectedCity = 'All Cities',
  search = '',
}) => {
  const [activeCategory, setActiveCategory] =
    useState('outings');

  const filteredExperiences = useMemo(() => {
    const normalizedSearch = normalizeValue(search);

    return experiences.filter((experience) => {
      const cityMatch =
        selectedCity === 'All Cities' ||
        normalizeValue(experience.city) ===
          normalizeValue(selectedCity);

      const experienceCategories = [
        ...(Array.isArray(experience.primaryCategories)
          ? experience.primaryCategories
          : []),
        experience.primaryCategory,
        experience.category,
      ]
        .filter(Boolean)
        .map(normalizeValue);

      const allowedCategoryNames =
        CATEGORY_ALIASES[activeCategory] || [];

      const categoryMatch =
        experienceCategories.length === 0
          ? activeCategory === 'outings'
          : experienceCategories.some((category) =>
              allowedCategoryNames.some(
                (allowedCategory) =>
                  category === allowedCategory ||
                  category.includes(allowedCategory)
              )
            );

      const searchableText = [
        experience.name,
        experience.city,
        experience.state,
        experience.category,
        experience.setting,
        experience.summary,
        experience.duration,
        experience.groupSize,
        experience.price,
        experiencePriceLabel(experience),
        ...(Array.isArray(experience.vibe)
          ? experience.vibe
          : []),
        ...(Array.isArray(experience.primaryCategories)
          ? experience.primaryCategories
          : []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const searchMatch =
        normalizedSearch === '' ||
        searchableText.includes(normalizedSearch);

      return cityMatch && categoryMatch && searchMatch;
    });
  }, [selectedCity, search, activeCategory]);

  return (
    <section className="experience-browser">
      <div className="experience-browser-inner">
        <div className="browse-results-header">
          <div className="browse-results-title">
            <p className="browse-results-eyebrow">
              Browse All Experiences
            </p>

            <h2>Browse all experiences</h2>
          </div>

          <div className="browse-category-tabs">
            <CategoryTabs
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          </div>
        </div>

        {filteredExperiences.length > 0 ? (
          <div className="browse-experience-grid">
            {filteredExperiences.map((experience) => {
              const location = [
                experience.city,
                experience.state,
              ]
                .filter(Boolean)
                .join(', ');

              return (
                <Link
                  to={`/business/${experience.id}`}
                  className="browse-experience-card"
                  key={experience.id}
                >
                  <div className="browse-card-image">
                    <img
                      src={experience.image}
                      alt={experience.name}
                    />
                  </div>

                  <div className="browse-card-body">
                    <div className="browse-card-location">
                      {location}
                    </div>

                    <h3>{experience.name}</h3>

                    <p className="browse-card-summary">
                      {experience.summary}
                    </p>

                    <div className="browse-card-details">
                      {experience.groupSize && (
                        <span>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            aria-hidden="true"
                          >
                            <circle cx="9" cy="8" r="3" />
                            <path d="M3.5 18v-1.5A4.5 4.5 0 0 1 8 12h2a4.5 4.5 0 0 1 4.5 4.5V18" />
                            <circle cx="17" cy="9" r="2.2" />
                            <path d="M15.5 13.5h1.7a3.3 3.3 0 0 1 3.3 3.3V18" />
                          </svg>

                          {experience.groupSize} people
                        </span>
                      )}

                      {experience.duration && (
                        <span>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            aria-hidden="true"
                          >
                            <circle cx="12" cy="12" r="8" />
                            <path d="M12 7.5V12l3 2" />
                          </svg>

                          {experience.duration}
                        </span>
                      )}

                      <span className="browse-card-price">
                        {experiencePriceLabel(experience)}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="browse-empty-state">
            <strong>
              No exact matches for these filters.
            </strong>

            <p>
              Try selecting another city, category, or using a
              broader search term.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ExperienceBrowser;
